import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaCircleCheck } from 'react-icons/fa6';
import { postJSON } from '../lib/api';

const COURSE_SLUG = 'ai-for-parents';

export default function PilotAccess() {
  useEffect(() => {
    document.title = 'Pilot Access — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  const [form, setForm] = useState({ code: '', email: '', firstName: '', lastName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setAlreadyEnrolled(false);

    const { code, email, firstName, lastName } = form;
    if (!code.trim() || !email.trim() || !firstName.trim() || !lastName.trim()) {
      setError('All fields are required.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await postJSON('/api/pilot/redeem', {
        code: code.trim(), email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(),
      });
      if (data.ok) {
        setSuccess(data.message || `Access granted! Check ${email.trim()} for your sign-in link.`);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      if (err.message && err.message.includes('already has access')) {
        setAlreadyEnrolled(true);
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-bone min-h-screen">
      <div className="border-b border-ink/10 bg-bone">
        <div className="container-page py-4">
          <Link
            to={`/courses/${COURSE_SLUG}`}
            className="inline-flex items-center gap-2 text-sm text-slate2 hover:text-ink"
          >
            <FaArrowLeft className="text-[10px]" />
            <span className="font-display text-base">Generative AI for Parents</span>
          </Link>
        </div>
      </div>

      <div className="container-page py-12 lg:py-16 max-w-lg mx-auto">
        <div className="eyebrow mb-2">Pilot program</div>
        <h1 className="font-display text-3xl lg:text-4xl leading-tight text-ink">
          Redeem your pilot access
        </h1>
        <p className="mt-4 text-slate2 leading-relaxed">
          Enter your pilot code and your name exactly as you'd like it to appear on your
          Certificate of Completion. Your name is stored at enrollment and cannot be changed later.
        </p>

        {success ? (
          <div className="mt-10 rounded-2xl bg-brand-sage/20 border border-brand-deep/20 p-8 text-center">
            <FaCircleCheck className="mx-auto text-brand-deep text-3xl mb-4" />
            <div className="font-display text-xl text-brand-deep">You're in!</div>
            <p className="mt-3 text-slate2 leading-relaxed">{success}</p>
            <p className="mt-2 text-sm text-slate2">
              The link works once and expires in 72 hours.
            </p>
          </div>
        ) : alreadyEnrolled ? (
          <div className="mt-10 rounded-2xl border border-brand-deep/20 bg-brand-sage/10 p-8 text-center">
            <FaCircleCheck className="mx-auto text-brand-deep text-3xl mb-4" />
            <div className="font-display text-xl text-brand-deep">You already have access</div>
            <p className="mt-3 text-slate2 leading-relaxed">
              This email address already has access to the course. Check your inbox for a sign-in link, or{' '}
              <Link to="/login" className="underline underline-offset-2 hover:text-ink">
                sign in to your account
              </Link>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div>
              <label className="block text-xs text-slate2 mb-1.5" htmlFor="pilot-code">
                Pilot code
              </label>
              <input
                id="pilot-code"
                type="text"
                required
                value={form.code}
                onChange={update('code')}
                placeholder="Enter your access code"
                autoComplete="off"
                className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink font-mono uppercase tracking-widest focus:outline-none focus:border-brand-deep/60 transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate2 mb-1.5" htmlFor="pilot-fn">
                  First name
                </label>
                <input
                  id="pilot-fn"
                  type="text"
                  required
                  value={form.firstName}
                  onChange={update('firstName')}
                  placeholder="Alex"
                  autoComplete="given-name"
                  className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-slate2 mb-1.5" htmlFor="pilot-ln">
                  Last name
                </label>
                <input
                  id="pilot-ln"
                  type="text"
                  required
                  value={form.lastName}
                  onChange={update('lastName')}
                  placeholder="Morgan"
                  autoComplete="family-name"
                  className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate2 mb-1.5" htmlFor="pilot-email">
                Email address
              </label>
              <input
                id="pilot-email"
                type="email"
                required
                value={form.email}
                onChange={update('email')}
                placeholder="alex@example.com"
                autoComplete="email"
                className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
              />
              <p className="mt-1.5 text-[11px] text-slate2">
                Your sign-in link will be sent here.
              </p>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-5 py-4">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-wait inline-flex"
            >
              {submitting ? 'Verifying…' : (
                <>
                  Claim free access <FaArrowRight className="text-xs" />
                </>
              )}
            </button>

            <p className="text-xs text-slate2 text-center">
              No payment required · Full course access for pilot participants
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
